/**
 * Subscribing this browser to push notifications.
 *
 * One subscription per browser, not per user: the endpoint the push service
 * hands out belongs to this installation of this browser, which is why the
 * server stores several rows for the same person.
 *
 * All of it degrades quietly. Push is missing outside a secure context, absent
 * in older Safari, and refused outright by anyone who has denied notifications
 * — none of which is worth an error on screen.
 */

const SUBSCRIBE_URL = '/api/push-subscriptions'
const STATUS_URL = '/api/push-subscriptions/status'

/**
 * How long to wait for the service worker before giving up on it.
 *
 * `navigator.serviceWorker.ready` never settles when no worker is registered,
 * and registration fails for reasons we cannot see from here: a 404 on
 * /sw.js, a first load with no network, a browser configured to refuse
 * workers. Awaiting it bare left the enable button disabled for good and the
 * whole push section hidden, with nothing on screen to explain either.
 */
const READY_TIMEOUT_MS = 5000

export const isPushSupported = (target: Window = window): boolean =>
  'serviceWorker' in target.navigator &&
  'PushManager' in target &&
  'Notification' in target

export const permissionState = (
  target: Window = window,
): NotificationPermission | 'unsupported' =>
  isPushSupported(target) ? target.Notification.permission : 'unsupported'

/**
 * The VAPID key travels as base64url in a meta tag and has to reach
 * `subscribe()` as bytes.
 */
export const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const raw = atob(padded)

  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)))
}

export const publicKeyFromDocument = (doc: Document = document): string =>
  doc
    .querySelector('meta[name="vapid-public-key"]')
    ?.getAttribute('content')
    ?.trim() ?? ''

type Http = {
  post: (url: string, body: unknown) => Promise<unknown>
  get: (url: string, config: unknown) => Promise<{ data: unknown }>
  delete: (url: string, config: { data: unknown }) => Promise<unknown>
}

type Deps = {
  target?: Window
  doc?: Document
  timeout?: number
  post?: (url: string, body: unknown) => Promise<unknown>
  get?: (url: string, config: unknown) => Promise<{ data: unknown }>
  remove?: (url: string, config: { data: unknown }) => Promise<unknown>
}

const http = (): Http => (window as unknown as { axios: Http }).axios

/**
 * The registration, or null when the worker never turned up. Everything here
 * goes through this rather than awaiting `ready` directly, so that a browser
 * without a working service worker reports "no push" instead of hanging.
 */
const readyRegistration = async (
  target: Window,
  timeout: number,
): Promise<ServiceWorkerRegistration | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined

  try {
    return await Promise.race([
      target.navigator.serviceWorker.ready,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeout)
      }),
    ])
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Asks for permission and registers the browser. Returns false for every
 * outcome that is not a live subscription, including the user saying no.
 *
 * The permission request has to come from a click: both Safari and Chrome
 * refuse it otherwise, and a page that asks on load gets denied for good.
 */
export const subscribeToPush = async ({
  target = window,
  doc = document,
  timeout = READY_TIMEOUT_MS,
  post = (url, body) => http().post(url, body),
}: Deps = {}): Promise<boolean> => {
  if (!isPushSupported(target)) {
    return false
  }

  const publicKey = publicKeyFromDocument(doc)
  if (publicKey === '') {
    // The installation has no VAPID keys, so there is nothing to subscribe to.
    return false
  }

  const permission = await target.Notification.requestPermission()
  if (permission !== 'granted') {
    return false
  }

  const registration = await readyRegistration(target, timeout)
  if (registration === null) {
    return false
  }

  const applicationServerKey = urlBase64ToUint8Array(publicKey)

  let subscription: PushSubscription
  try {
    subscription = await registration.pushManager.subscribe({
      // Required by Chrome: every push has to result in something visible.
      userVisibleOnly: true,
      applicationServerKey,
    })
  } catch {
    /**
     * `subscribe()` throws InvalidStateError when this browser already holds a
     * subscription issued under a different VAPID key — which is what an
     * installation that regenerated its keys looks like from here. The old
     * subscription can no longer receive anything, so it is dropped and the
     * browser subscribes afresh rather than being stuck forever.
     */
    const stale = await registration.pushManager.getSubscription()
    if (stale === null) {
      return false
    }

    try {
      await stale.unsubscribe()
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      })
    } catch {
      return false
    }
  }

  await post(SUBSCRIBE_URL, subscription.toJSON())

  return true
}

/**
 * Unsubscribes this browser. The server row goes first: a subscription the
 * browser has dropped but the server still believes in produces pushes into
 * the void, while the opposite is merely a stale row.
 */
export const unsubscribeFromPush = async ({
  target = window,
  timeout = READY_TIMEOUT_MS,
  remove = (url, config) => http().delete(url, config),
}: Deps = {}): Promise<boolean> => {
  if (!isPushSupported(target)) {
    return false
  }

  const registration = await readyRegistration(target, timeout)
  if (registration === null) {
    return false
  }

  const subscription = await registration.pushManager.getSubscription()

  if (subscription === null) {
    return false
  }

  await remove(SUBSCRIBE_URL, { data: { endpoint: subscription.endpoint } })
  await subscription.unsubscribe()

  return true
}

export const currentSubscription = async (
  target: Window = window,
  timeout: number = READY_TIMEOUT_MS,
): Promise<PushSubscription | null> => {
  if (!isPushSupported(target)) {
    return null
  }

  const registration = await readyRegistration(target, timeout)

  return registration === null
    ? null
    : registration.pushManager.getSubscription()
}

export type PushState = 'unsupported' | 'default' | 'denied' | 'subscribed'

/**
 * @brief What this browser's push state actually is, having checked with the
 *        server rather than trusting what the browser holds.
 *
 * A browser subscription outlives the session that created it, and the two
 * ends drift apart in both directions:
 *
 * - Someone signs in after a colleague signed out. The endpoint in the browser
 *   is still the colleague's, and their tickets would go on arriving on a
 *   phone that is no longer theirs.
 * - The server row was deleted, because the push service answered 410 for it
 *   once and the channel pruned it. The browser still reports a subscription
 *   that nothing will ever be sent to.
 *
 * Both end the same way: the browser subscription is dropped and the user is
 * offered the choice again. Taking over someone else's subscription silently
 * would carry their answer over to a person who never gave one.
 */
export const syncSubscription = async ({
  target = window,
  timeout = READY_TIMEOUT_MS,
  get = (url, config) => http().get(url, config),
}: Deps = {}): Promise<PushState> => {
  if (!isPushSupported(target)) {
    return 'unsupported'
  }

  if (target.Notification.permission === 'denied') {
    return 'denied'
  }

  const subscription = await currentSubscription(target, timeout)
  if (subscription === null) {
    return 'default'
  }

  let subscribed: boolean
  try {
    const response = await get(STATUS_URL, {
      params: { endpoint: subscription.endpoint },
    })
    subscribed =
      (response.data as { subscribed?: boolean } | undefined)?.subscribed ===
      true
  } catch {
    /**
     * The server could not be asked — offline, or a session that expired. Say
     * nothing and change nothing: unsubscribing on a failed request would turn
     * a lost connection into a lost subscription.
     */
    return 'subscribed'
  }

  if (subscribed) {
    return 'subscribed'
  }

  await subscription.unsubscribe()

  return 'default'
}
